import React, { useState, useEffect, useCallback } from 'react'
import {
  Box,
  Typography,
  Button,
  Checkbox,
  FormControlLabel,
  FormGroup,
  TextField,
  CircularProgress,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  InputAdornment,
} from '@mui/material'
import {
  ExpandMore as ExpandIcon,
  Search as SearchIcon,
  Save as SaveIcon,
} from '@mui/icons-material'
import { interventionConfigsAPI, riskCodesAPI } from '../../services/api'

function RiskCodesTab({ interventionId, canEdit, onError }) {
  const [attachedRiskCodes, setAttachedRiskCodes] = useState([])
  const [allRiskCodes, setAllRiskCodes] = useState([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedCodes, setSelectedCodes] = useState(new Set())
  const [hasChanges, setHasChanges] = useState(false)

  const fetchData = useCallback(async () => {
    try {
      setLoading(true)
      const [attached, all] = await Promise.all([
        interventionConfigsAPI.getRiskCodes(interventionId),
        riskCodesAPI.getAll().then(res => res.data || res),
      ])
      setAttachedRiskCodes(attached)
      setAllRiskCodes(all)
      setSelectedCodes(new Set(attached.map(rc => rc.id)))
      setHasChanges(false)
    } catch (err) {
      console.error('Error fetching risk codes:', err)
      onError?.('Failed to load risk codes')
    } finally {
      setLoading(false)
    }
  }, [interventionId, onError])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const handleToggleRiskCode = (riskCodeId) => {
    const newSelected = new Set(selectedCodes)
    if (newSelected.has(riskCodeId)) {
      newSelected.delete(riskCodeId)
    } else {
      newSelected.add(riskCodeId)
    }
    setSelectedCodes(newSelected)
    setHasChanges(true)
  }

  const handleSave = async () => {
    try {
      setSaving(true)
      const riskCodesToSave = Array.from(selectedCodes).map(id => ({
        risk_code_id: id,
      }))
      await interventionConfigsAPI.setRiskCodes(interventionId, riskCodesToSave)
      await fetchData()
    } catch (err) {
      console.error('Error saving risk codes:', err)
      onError?.('Failed to save risk codes')
    } finally {
      setSaving(false)
    }
  }

  // Group risk codes by category
  const groupedRiskCodes = allRiskCodes.reduce((acc, rc) => {
    const category = rc.category || 'general'
    if (!acc[category]) acc[category] = []
    acc[category].push(rc)
    return acc
  }, {})

  // Filter by search query
  const filterRiskCodes = (codes) => {
    if (!searchQuery) return codes
    const query = searchQuery.toLowerCase()
    return codes.filter(rc =>
      rc.name.toLowerCase().includes(query) ||
      rc.code.toLowerCase().includes(query) ||
      rc.description?.toLowerCase().includes(query)
    )
  }

  const categoryLabels = {
    mental_health: 'Mental Health',
    physical: 'Physical Symptoms',
    treatment: 'Treatment-Related',
    engagement: 'Engagement',
    cognitive: 'Cognitive',
    general: 'General',
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={3}>
        <CircularProgress size={24} />
      </Box>
    )
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
        <Box>
          <Typography variant="body2" color="text.secondary">
            {selectedCodes.size} of {allRiskCodes.length} risk codes selected
          </Typography>
        </Box>
        {canEdit && (
          <Button
            size="small"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={!hasChanges || saving}
            variant={hasChanges ? 'contained' : 'text'}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        )}
      </Box>

      <TextField
        fullWidth
        size="small"
        placeholder="Search risk codes..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        sx={{ mb: 1.5 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" color="action" />
            </InputAdornment>
          ),
        }}
      />

      {Object.entries(groupedRiskCodes).map(([category, codes]) => {
        const filteredCodes = filterRiskCodes(codes)
        if (filteredCodes.length === 0) return null

        const selectedInCategory = filteredCodes.filter(rc => selectedCodes.has(rc.id)).length

        return (
          <Accordion key={category} defaultExpanded={selectedInCategory > 0}>
            <AccordionSummary expandIcon={<ExpandIcon />}>
              <Box display="flex" alignItems="center" gap={1.5}>
                <Typography variant="body2" fontWeight={500}>
                  {categoryLabels[category] || category}
                </Typography>
                <Typography variant="caption" color={selectedInCategory > 0 ? 'primary.main' : 'text.secondary'}>
                  {selectedInCategory}/{filteredCodes.length}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0 }}>
              <FormGroup>
                {filteredCodes.map(rc => (
                  <Box
                    key={rc.id}
                    sx={{
                      py: 0.5,
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      '&:last-child': { borderBottom: 'none' }
                    }}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={selectedCodes.has(rc.id)}
                          onChange={() => handleToggleRiskCode(rc.id)}
                          disabled={!canEdit}
                          size="small"
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body2">
                            {rc.name}
                            <Typography
                              component="span"
                              variant="caption"
                              color="text.secondary"
                              sx={{ ml: 1 }}
                            >
                              ({rc.code})
                            </Typography>
                          </Typography>
                          {rc.description && (
                            <Typography variant="caption" color="text.secondary">
                              {rc.description}
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                  </Box>
                ))}
              </FormGroup>
            </AccordionDetails>
          </Accordion>
        )
      })}

      {allRiskCodes.length === 0 && (
        <Alert severity="info">
          No risk codes are configured in the system. Create risk codes in the Risk Codes section first.
        </Alert>
      )}
    </Box>
  )
}

export default RiskCodesTab
